import React, { FC, useState } from 'react';

import { Box, Center, HStack, Stack, Text } from '@chakra-ui/react';
import { map } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { CustomTooltip, getProviderAlias } from 'components/DataDisplay';
import { GettingStartedComponent } from 'components/DataDisplay/Utils/GettingStarted/GettingStartedComponent';
import { GettingStartedList } from 'components/DataDisplay/Utils/GettingStarted/GettingStartedList';
import { IconTypes } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { getGreeting } from 'components/index';

import { selectApp } from '../selectors';

import { gettingStartedAboutAriksa } from './utils';

export const GettingStarted: FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector(selectApp);
  const [showList, setShowList] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const listComponent = list => (
    <Stack spacing={10}>
      {map(list, o => (
        <Text key={o}>{o}</Text>
      ))}
    </Stack>
  );

  const steps = [
    // {
    //   label: 'Onboard cloud accounts',
    //   key: 'onboard',
    //   component: (
    //     <Center pb={6}>
    //       <Stack spacing={4} w="full">
    //         {map(icons, (o, index) => (
    //           <HStack
    //             justify="space-around"
    //             w="full"
    //             key={'getting-started-onboard-cloud-account-' + index}
    //           >
    //             {map(o, icon => (
    //               <Box
    //                 boxSize={20}
    //                 border="1px solid"
    //                 borderRadius="md"
    //                 borderColor={customTheme.colors.gray['100']}
    //                 p={5}
    //                 key={icon}
    //                 bg={'rgba(255, 255, 255, 0.25)'}
    //               >
    //                 <Center pt={icon === IconTypes.Aws ? 2 : 0}>
    //                   {getIcon(icon)}
    //                 </Center>
    //               </Box>
    //             ))}
    //           </HStack>
    //         ))}
    //       </Stack>
    //     </Center>
    //   ),
    // },
    {
      label: 'Review prioritized risks and attack paths on the dashboard',
      key: 'risks',
      header: 'Detect & Identify with Depth',
      component: listComponent([
        `Ariksa maximizes observability of runtime risks
            and threat assessment across runtime events,
            app interactions, identity privileges, sensitive
            data, credential leakage, and software
            vulnerabilities.`,
        `We deliver fewer, high-quality, prioritized issues
            to turbocharge your cloud security journey.`,
      ]),
    },
    {
      label:
        'Use security graphs to investigate visually with 360° risk context',
      key: 'alerts',
      header: 'Auto-Triage & Investigate with Graphs',
      component: listComponent([
        `Before Ariksa creates findings, we automatically triage across
            mitigating factors to suppress unnecessary issues based on exposure,
            malicious sources, vulnerabilities, network traffic, actions, type
            of environment etc.`,
        `For real issues, we generate security graphs enriched with 360º risk
            context and dependencies for rapid investigation.`,
      ]),
    },
    {
      label:
        'Remediate with a single-click or automatically for rapid response',
      key: 'remediate',
      header: 'Automated Remediation',
      component: listComponent([
        `Ariksa provides a large library of out-of-the-box remediations to
            accelerate response and reduce MTTR. These can be executed manually
            or automatically with policy guardrails.`,

        `You can create custom remediations that can be attached to any
            policy for customized response and workflows based on organizational
            policies.`,
      ]),
    },
    {
      label: 'Use Ariksa AI for threat hunting, triaging and custom insights',
      key: 'ai',
      header: 'AI Capabilities to Detect, Triage and Fix',
      component: listComponent([
        `We provide AI driven search built on our Knowledge Fabric™ that
            stores high-quality, highly correlated data from 1st party and 3rd
            party sources that is enriched with business and environmental
            context.`,

        `This differentiates us from simple chatbots and is the foundation
            for hallucination-free AI responses and actions.`,
      ]),
    },
  ];

  const renderCloud = provider => {
    return (
      <CustomTooltip
        label={`Onboard ${getProviderAlias(provider)}`}
        styles={{ text: { overflow: 'visible', textOverflow: 'clip' } }}
      >
        <Box
          h={32}
          w={56}
          border="1px solid"
          borderRadius={8}
          borderColor={customTheme.colors.gray['100']}
          color="white"
          fontSize="lg"
          transform="scale(1)"
          transition="all 0.3s ease-in-out"
          _hover={{ transform: 'scale(1.1)', py: 5 }}
          cursor="pointer"
          bg={customTheme.colors.gettingStartedItemBg}
          onClick={() => navigate('/setup/accounts/add/' + provider)}
        >
          <Center w="full" h="full">
            <Center boxSize={10}>{getIcon(provider)}</Center>
          </Center>
        </Box>
      </CustomTooltip>
    );
  };

  const renderGettingStartedText = (
    text: string,
    onClick?: () => void,
    pt?: number,
  ) => (
    <Box
      color="white"
      fontSize="20px"
      pt={pt}
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      fontWeight={600}
    >
      {text}
    </Box>
  );

  return (
    <GettingStartedComponent
      // Updated: getGreeting() uses the current time instead of the load-time constant.
      header={getGreeting() + ' ' + user.info.username}
      content={
        <Stack pl={14} spacing={6}>
          {renderGettingStartedText('Choose cloud to onboard account')}
          <HStack spacing={10}>
            {renderCloud(IconTypes.Aws)}
            {renderCloud(IconTypes.Azure)}
            {renderCloud(IconTypes.Gcp)}
          </HStack>
          {renderGettingStartedText(
            'What happens after I onboard my accounts >>',
            () => {
              setShowDetails(false);
              setShowList(!showList);
            },
            6,
          )}
          {showList && (
            <GettingStartedList
              steps={steps}
              styles={{
                stepWidth: '2xl',
                lineLeftPadding: '33px',
                buttonPaddingLeft: 0,
                listPaddingTop: 4,
              }}
            />
          )}
          {renderGettingStartedText(
            'About Ariksa >>',
            () => {
              setShowDetails(!showDetails);
              setShowList(false);
            },
            showList ? 6 : 0,
          )}
          {showDetails && gettingStartedAboutAriksa()}
        </Stack>
      }
    />
  );
};
